import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { useLocation } from 'react-router-dom'
import { ArcVoiceDevice, ArcVoiceSpeaker } from '@common/interfaces/arcVoice'
import { IAtlasAISuggestion } from '@common/interfaces/atlasai'
import api from '../api'
import { SnackbarType } from '../components/Common/Snackbar'
import { arcPresence } from '../helpers/arcPresence/arcPresenceStore'
import {
  ArcVoiceCall,
  ArcVoiceCallStatus,
  IArcVoiceCallDeps,
  IArcVoiceCallSnapshot,
} from '../helpers/arcVoice/arcVoiceCall'
import { isCallOpen } from '../helpers/arcVoice/callPresentation'
import { createGoTo } from '../helpers/arcVoice/goTo'
import {
  IArcVoiceCaption,
  IRealtimeVoiceHandlers,
  RealtimeVoiceClient,
} from '../helpers/arcVoice/realtimeVoiceClient'
import { RoomInsightGate } from '../helpers/arcVoice/roomInsight'
import { isAddressedInRoom } from '../helpers/arcVoice/roomTurns'
import { watchScreenText } from '../helpers/arcVoice/screenText'
import { openMicrophone } from '../helpers/audio/microphone'
import { huddleDevices } from '../helpers/huddle/huddleDevices'
import { lineWindowMs } from '../helpers/huddle/huddleSpeakers'
import { useIsMobile } from '../hooks/useIsMobile'
import useScreenWakeLock from '../hooks/useScreenWakeLock'
import { canCaptureAudio, VoiceRecordingError } from '../hooks/useVoiceRecording'
import { isMuteVoiceCallChord } from '../pages/Messages/keyboardShortcuts'
import { currentPageUrl } from '../redux/channels/actions'
import { useDispatch } from '../redux/hooks'
import { putNotification } from '../redux/ui/actions'

const MIC_ERROR_MESSAGES: Record<VoiceRecordingError, string> = {
  [VoiceRecordingError.PermissionDenied]:
    'Microphone access is blocked. Allow it in your browser settings to talk to Arc.',
  [VoiceRecordingError.NoMicrophone]: 'No microphone found. Connect one to talk to Arc.',
  [VoiceRecordingError.Unsupported]:
    'This browser can’t stream audio. Try Chrome or Safari, or type to Arc instead.',
  [VoiceRecordingError.Failed]: 'Couldn’t start the microphone. Please try again.',
}

/** The mic chosen for huddles, which is this browser's one mic choice. */
const getMicrophone = (): Promise<MediaStream> =>
  openMicrophone({ deviceId: huddleDevices.get().inputId, messages: MIC_ERROR_MESSAGES })

/**
 * Arc brought into a huddle from this browser: it hears the room instead of the mic,
 * and its voice goes out to the others as well as to these speakers.
 */
export interface IArcVoiceRoom {
  huddleId: string
  /** A fresh stream of the room each time: a connection stops its input when it closes. */
  getRoomAudio: () => MediaStream
  /** Arc's voice, for the huddle to send on. Called once per connection. */
  attachArcVoice: (stream: MediaStream) => void
  /** Whether Arc has something worth saying unasked; see RoomInsightGate. */
  judgeInsight: (lines: string[], arcSaid: string[]) => Promise<string | null>
  /** Something Arc said, for the huddle transcript. */
  recordArcLine: (text: string) => void
  /** Who said a line just captioned, by user id, over the last `windowMs`. */
  whoIsSpeaking: (windowMs: number) => string | null
}

interface IRoomAsker {
  userId: string
  lineMs: number
  saidAt: number
}

/** Who a line addressed to Arc came from; nobody clearly speaking is null, so it runs as the host. */
const trackAsker = (
  room: IArcVoiceRoom,
  caption: IArcVoiceCaption,
  previous: IRoomAsker | null,
): IRoomAsker | null => {
  const isAsk =
    caption.isFinal && caption.speaker === ArcVoiceSpeaker.User && isAddressedInRoom(caption.text)
  if (!isAsk) return previous
  const windowMs = lineWindowMs(caption.text)
  const userId = room.whoIsSpeaking(windowMs)
  return userId ? { userId, lineMs: windowMs, saidAt: Date.now() } : null
}

/** A connection that hears the room, speaks into it, and lets go of both however it closes. */
const createRoomConnection = (
  handlers: IRealtimeVoiceHandlers,
  room: IArcVoiceRoom,
  onCaption: (caption: IArcVoiceCaption) => void,
): RealtimeVoiceClient => {
  let connection: RealtimeVoiceClient | null = null
  let unfollowSpeaker = () => {}
  const gate = new RoomInsightGate({
    judge: room.judgeInsight,
    speak: (prompt) => connection?.prompt(prompt),
    isBusy: () => connection?.isBusy ?? true,
  })
  const release = () => {
    gate.close()
    unfollowSpeaker()
  }
  const voice = new RealtimeVoiceClient({
    ...handlers,
    onCaption: (caption) => {
      onCaption(caption)
      handlers.onCaption(caption)
      gate.hear(caption)
      if (caption.isFinal && caption.speaker === ArcVoiceSpeaker.Arc)
        room.recordArcLine(caption.text)
    },
    onDisconnect: () => {
      release()
      handlers.onDisconnect()
    },
    onHangUp: release,
  })
  connection = voice
  voice.setOutputDevice(huddleDevices.get().outputId)
  unfollowSpeaker = huddleDevices.subscribe(({ outputId }) => voice.setOutputDevice(outputId))
  room.attachArcVoice(voice.captureOutput())
  return voice
}

/** The same call, hearing the huddle and speaking into it. */
const roomDeps = (
  deps: IArcVoiceCallDeps,
  room: IArcVoiceRoom,
  device: ArcVoiceDevice,
): IArcVoiceCallDeps => {
  // Whoever last spoke to Arc: what they ask runs as them, with their access.
  let asker: IRoomAsker | null = null
  return {
    ...deps,
    getMicrophone: () => Promise.resolve(room.getRoomAudio()),
    createConnection: (handlers) =>
      createRoomConnection(handlers, room, (caption) => {
        asker = trackAsker(room, caption, asker)
      }),
    startSession: (channelId) =>
      api.channels.startArcVoice(channelId, currentPageUrl(), device, { huddleId: room.huddleId }),
    ask: (channelId, request, callRecordId) =>
      api.channels.askArcByVoice(
        channelId,
        request,
        currentPageUrl(),
        callRecordId,
        device,
        asker
          ? { userId: asker.userId, lineMs: asker.lineMs, ageMs: Date.now() - asker.saidAt }
          : undefined,
      ),
  }
}

interface IStartArcVoiceOptions {
  /** The huddle to bring Arc into, instead of calling it alone. */
  room?: IArcVoiceRoom
  /** The earlier call row in the conversation that this call picks up. */
  rejoinCallId?: string
}

interface IArcVoiceCallContext {
  call: ArcVoiceCall | null
  /** Null while no call is open. */
  snapshot: IArcVoiceCallSnapshot | null
  /**
   * Call inside the click that asked for it. `channel` is the conversation, or a
   * function that creates one, run only once the call is sure to start. Returns the call it
   * opened, or null when it opened none.
   */
  start: (
    channel: string | (() => Promise<string>),
    options?: IStartArcVoiceOptions,
  ) => ArcVoiceCall | null
  /** The huddle the open call was brought into, if it was. */
  roomHuddleId: string | null
  /** Call Arc in a new conversation with it, which `onOpened` shows once it exists. */
  startInNewConversation: (onOpened: (channelId: string) => void) => void
  /** The call is running but its stage is put away, in favour of the chat. */
  isMinimized: boolean
  setIsMinimized: (isMinimized: boolean) => void
  /** Whether a call stage is on screen, so the floating pill knows to step aside. */
  isStageVisible: boolean
  /** Mounted stages register here; returns the unregister. */
  registerStage: () => () => void
  /** The Arc session whose approvals the call's chat shows inline right now, so the dock skips them. */
  callChatSessionId: string | null
  /** A chat showing the call's conversation registers its session here; returns the unregister. */
  registerCallChat: (sessionId: string) => () => void
}

const ArcVoiceCallContext = createContext<IArcVoiceCallContext | null>(null)

interface IArcVoiceCallStarter {
  start: IArcVoiceCallContext['start']
  /** A call is open in this tab. */
  isOnCall: boolean
}

/** Changes only as a call opens or ends, so the timeline's call rows don't re-render per caption. */
const ArcVoiceCallStarterContext = createContext<IArcVoiceCallStarter>({
  start: () => null,
  isOnCall: false,
})

/**
 * Calls used to be kept in this browser for a reload to bring back; a call is now
 * rejoined from its row instead. What a browser still holds of one carries a
 * transcript, so it goes on the next load.
 */
const removeSavedCalls = () => {
  try {
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith('arc-voice-call'))
      .forEach((key) => window.localStorage.removeItem(key))
  } catch {
    // Storage blocked: nothing was written there either.
  }
}

/** A page settles before it is described, so clicking through tabs isn't a request per click. */
const PAGE_CONTEXT_DEBOUNCE_MS = 600

const subscribeNothing = (): (() => void) => () => {}
const getNoSnapshot = (): IArcVoiceCallSnapshot | null => null

/**
 * The one voice call with Arc, held above the pages so it outlives navigation:
 * open a client page mid-call and the call carries on, with a pill to get back.
 */
export const ArcVoiceCallProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch()
  const [call, setCall] = useState<ArcVoiceCall | null>(null)
  // The device the call started on, which a mid-call resize does not change.
  const [callDevice, setCallDevice] = useState(ArcVoiceDevice.Desktop)
  const [isMinimized, setIsMinimized] = useState(false)
  const [stageCount, setStageCount] = useState(0)
  const callRef = useRef<ArcVoiceCall | null>(null)

  const snapshot = useSyncExternalStore(
    call?.subscribe ?? subscribeNothing,
    call?.getSnapshot ?? getNoSnapshot,
  )

  const notify = useCallback(
    (message: string, isError: boolean) => {
      dispatch(putNotification({ message, type: isError ? SnackbarType.Error : SnackbarType.Info }))
    },
    [dispatch],
  )

  // The phone layout, where the call fills the screen: a call keeps the one it started on.
  const device = useIsMobile() ? ArcVoiceDevice.Mobile : ArcVoiceDevice.Desktop
  const deps = useMemo<IArcVoiceCallDeps>(
    () => ({
      createConnection: (handlers) => new RealtimeVoiceClient(handlers),
      getMicrophone,
      startSession: (channelId, rejoinCallId) =>
        api.channels.startArcVoice(channelId, currentPageUrl(), device, { rejoinCallId }),
      ask: (channelId, request, callRecordId) =>
        api.channels.askArcByVoice(channelId, request, currentPageUrl(), callRecordId, device),
      getAnswer: (channelId, chatHistoryId, knownProgress) =>
        api.channels.getArcVoiceAnswer(channelId, chatHistoryId, knownProgress),
      createRecord: (channelId, record) => api.channels.recordArcVoiceCall(channelId, record),
      checkpoint: async (channelId, recordId, checkpoint, isUnloading) => {
        const saved = await api.channels.checkpointArcVoiceCall(
          channelId,
          recordId,
          checkpoint,
          isUnloading,
        )
        return saved?.voiceCall?.status ?? null
      },
      stopRuns: async (sessionId, turnIds) => {
        const { turns } = await api.channels.cancelArcRun(sessionId, turnIds)
        return turns
      },
      turnEffects: (chatHistoryId) => arcPresence.turnEffects(chatHistoryId),
      goTo: createGoTo({
        // The api client already unwraps the response body's `data`.
        findRecords: async (name) =>
          ((await api.mentions.listSuggestions({ name, purpose: 'link' })) as
            IAtlasAISuggestion[] | undefined) ?? [],
        navigate: (url, label) => arcPresence.requestNavigation(url, label),
        currentPath: () => window.location.pathname,
      }),
      notify,
    }),
    [notify, device],
  )

  const [roomHuddleId, setRoomHuddleId] = useState<string | null>(null)
  const start = useCallback(
    (
      channel: string | (() => Promise<string>),
      { room, rejoinCallId }: IStartArcVoiceOptions = {},
    ): ArcVoiceCall | null => {
      const current = callRef.current
      if (current && current.getSnapshot().status !== ArcVoiceCallStatus.Ended) {
        if (current.getSnapshot().channelId === channel) setIsMinimized(false)
        else notify('You’re already on Arc Voice. Hang up first to start another.', true)
        return null
      }
      if (!canCaptureAudio()) {
        notify(MIC_ERROR_MESSAGES[VoiceRecordingError.Unsupported], true)
        return null
      }

      let next: ArcVoiceCall
      try {
        next = new ArcVoiceCall(room ? roomDeps(deps, room, device) : deps)
        void next.open(typeof channel === 'function' ? channel() : channel, rejoinCallId)
      } catch {
        notify(MIC_ERROR_MESSAGES[VoiceRecordingError.Unsupported], true)
        return null
      }
      callRef.current = next
      setRoomHuddleId(room?.huddleId ?? null)
      // In a huddle the room is the stage; the call keeps to its pill.
      setIsMinimized(!!room)
      setCallDevice(device)
      setCall(next)
      return next
    },
    [deps, device, notify],
  )

  const startInNewConversation = useCallback(
    (onOpened: (channelId: string) => void) =>
      // Lazy: a call refused (one is already on) must not create or open a conversation.
      start(() =>
        api.channels.openArcVoiceConversation().then(({ channelId }) => {
          onOpened(channelId)
          return channelId
        }),
      ),
    [start],
  )

  // Moving around the app mid-call: tell the voice what is on screen now.
  const { pathname, search } = useLocation()
  const callChannelId = snapshot?.channelId ?? null
  const isCallOn = isCallOpen(snapshot)
  useEffect(() => {
    if (!call || !callChannelId || !isCallOn) return undefined
    let isCurrent = true
    const timer = setTimeout(() => {
      api.channels
        .describeArcVoicePage(callChannelId, `${pathname}${search}`)
        .then(({ pageContext }) => {
          if (isCurrent) call.setPageContext(pageContext)
        })
        .catch(() => {})
    }, PAGE_CONTEXT_DEBOUNCE_MS)
    return () => {
      isCurrent = false
      clearTimeout(timer)
    }
  }, [call, callChannelId, isCallOn, pathname, search])

  // The call fills a phone's screen, so a page Arc opens there would take them off it.
  const isScreenHeld = isCallOn && callDevice === ArcVoiceDevice.Mobile
  useEffect(() => {
    arcPresence.holdNavigation(isScreenHeld)
    return () => arcPresence.holdNavigation(false)
  }, [isScreenHeld])

  // What is on screen, as text, so "the third one" or "what am I looking at" needs no lookup.
  useEffect(
    () => (call && isCallOn ? watchScreenText((text) => call.setScreenText(text)) : undefined),
    [call, isCallOn],
  )

  // Arc or the voice moved the screen: the voice knows where they are now, not after the
  // page has loaded and been described (which follows, above, and fills in the detail).
  useEffect(
    () =>
      call
        ? arcPresence.onNavigation((url, label) =>
            call.setPageContext(`${label ?? url} (just opened; it may still be loading)`),
          )
        : undefined,
    [call],
  )

  // Mute from anywhere in the app: the call carries on across pages, so its chord does too.
  useEffect(() => {
    if (!call || !isCallOn) return undefined
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isMuteVoiceCallChord(event)) return
      event.preventDefault()
      call.toggleMute()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [call, isCallOn])

  // A phone that locks suspends the page and drops the call; a live call keeps the screen on.
  useScreenWakeLock(snapshot?.status === ArcVoiceCallStatus.Live)

  // An approval answered mid-call: the agenda item it held up follows what Arc does next.
  useEffect(
    () =>
      call
        ? arcPresence.onApprovalResolved((resolution) => call.handleApprovalResolved(resolution))
        : undefined,
    [call],
  )

  // Arc replies in the call's conversation: background work that finishes is heard too.
  useEffect(
    () => (call ? arcPresence.onArcReply((reply) => call.handleArcReply(reply)) : undefined),
    [call],
  )

  // An ended call lets go, so the next one starts clean.
  useEffect(() => {
    if (snapshot?.status !== ArcVoiceCallStatus.Ended) return
    callRef.current = null
    setCall(null)
  }, [snapshot?.status])

  useEffect(removeSavedCalls, [])

  // A reload or closed tab: send what was said before the page goes, to rejoin from its row.
  useEffect(() => {
    const handlePageHide = () => callRef.current?.handlePageHide()
    window.addEventListener('pagehide', handlePageHide)
    return () => window.removeEventListener('pagehide', handlePageHide)
  }, [])

  // Leaving the app (signing out, the layout unmounting) hangs up.
  useEffect(() => () => void callRef.current?.end(), [])

  const registerStage = useCallback(() => {
    setStageCount((count) => count + 1)
    return () => setStageCount((count) => count - 1)
  }, [])

  const [callChatSessionId, setCallChatSessionId] = useState<string | null>(null)
  const registerCallChat = useCallback((sessionId: string) => {
    setCallChatSessionId(sessionId)
    return () => setCallChatSessionId((current) => (current === sessionId ? null : current))
  }, [])

  const value = useMemo<IArcVoiceCallContext>(
    () => ({
      call,
      snapshot,
      start,
      startInNewConversation,
      roomHuddleId,
      isMinimized,
      setIsMinimized,
      isStageVisible: stageCount > 0,
      registerStage,
      callChatSessionId,
      registerCallChat,
    }),
    [
      call,
      snapshot,
      start,
      startInNewConversation,
      roomHuddleId,
      isMinimized,
      stageCount,
      registerStage,
      callChatSessionId,
      registerCallChat,
    ],
  )

  const starter = useMemo<IArcVoiceCallStarter>(
    () => ({ start, isOnCall: isCallOn }),
    [start, isCallOn],
  )

  return (
    <ArcVoiceCallContext.Provider value={value}>
      <ArcVoiceCallStarterContext.Provider value={starter}>
        {children}
      </ArcVoiceCallStarterContext.Provider>
    </ArcVoiceCallContext.Provider>
  )
}

const NO_CALL: IArcVoiceCallContext = {
  call: null,
  snapshot: null,
  start: () => null,
  startInNewConversation: () => {},
  roomHuddleId: null,
  isMinimized: false,
  setIsMinimized: () => {},
  isStageVisible: false,
  registerStage: () => () => {},
  callChatSessionId: null,
  registerCallChat: () => () => {},
}

/** The call, or a no-op stand-in outside the provider (snapshot tests, the login screens). */
export const useArcVoiceCall = (): IArcVoiceCallContext =>
  useContext(ArcVoiceCallContext) ?? NO_CALL

/** Starting a call and whether one is open, without the snapshot that changes as it runs. */
export const useArcVoiceCallStarter = (): IArcVoiceCallStarter =>
  useContext(ArcVoiceCallStarterContext)
